// useChunkErrorHandler.js
import { useModal } from 'components/shared/modal';
import { useEffect } from 'react';
import ChunkReloadModalContent from 'widgets/update-banner/components/ChunkReloadModalContent';

const RELOAD_FLAG_KEY = 'chunk-reload-attempted';
const RELOAD_DELAY_MS = 3000; // time the modal stays visible before reload fires

// webpack sets error.name = 'ChunkLoadError' reliably — check that first.
// The regex fallback only matters for native dynamic import() rejections
// (e.g. if you ever add Vite-built micro-frontends), which surface as a
// generic TypeError with no distinct .name.
function isChunkError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;

  const message = error.message || '';
  return (
    /Loading chunk [\d]+ failed/.test(message) ||
    /Failed to fetch dynamically imported module/.test(message) ||
    /Importing a module script failed/.test(message)
  );
}

export function useChunkErrorHandler() {
  const modal = useModal();

  useEffect(() => {
    function handleChunkFailure(error) {
      if (!isChunkError(error)) return;

      const alreadyAttempted = sessionStorage.getItem(RELOAD_FLAG_KEY);
      if (alreadyAttempted) return; // guard: don't loop if the deploy is genuinely broken

      sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
      modal.openModal(ChunkReloadModalContent);
      setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    }

    function onError(event) {
      handleChunkFailure(event.error); // actual Error object, not event.message string
    }
    function onUnhandledRejection(event) {
      handleChunkFailure(event.reason); // actual rejection value, usually an Error
    }

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [modal]);
}
